import { useState, useRef, useCallback, memo, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { QRCodeSVG } from 'qrcode.react';
import { useLanguage } from '../../context/LanguageContext';
import { CoverPreview, CoverControls } from './CoverEditor';
import { convertHeicFileToJpeg, convertHeicFilesToJpeg, isUnsupportedRawFile, filterOutRawFiles } from '../../lib/heicConvert';
import {
  createMobileSessionId,
  getMobileUploadUrl,
  listMobileSessionPhotos,
  deleteMobileSessionPhotos,
} from '../../lib/mobileUploadSession';
import './PagesManager.css';

// Load Google Fonts for text elements
if (!document.getElementById('photobook-fonts')) {
  const link = document.createElement('link');
  link.id = 'photobook-fonts';
  link.rel = 'stylesheet';
  link.href = 'https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,700;1,400&family=Great+Vibes&family=Dancing+Script:wght@400;700&family=Lora:ital,wght@0,400;0,700;1,400&family=Montserrat:ital,wght@0,400;0,700&family=Oswald:wght@400;700&family=Open+Sans:ital,wght@0,400;0,700;1,400&display=swap';
  document.head.appendChild(link);
}

// Helper function to detect image orientation - with cleanup
const getImageOrientation = (photo) => {
  return new Promise((resolve) => {
    const img = new Image();
    const cleanup = () => {
      img.onload = null;
      img.onerror = null;
      img.src = ''; // Release memory
    };
    img.onload = () => {
      const isLandscape = img.width > img.height;
      const isSquare = Math.abs(img.width - img.height) < img.width * 0.1;
      const result = { isLandscape, isSquare, width: img.width, height: img.height };
      cleanup();
      resolve(result);
    };
    img.onerror = () => {
      cleanup();
      resolve({ isLandscape: true, isSquare: false, width: 0, height: 0 });
    };
    img.src = photo.src;
  });
};

// Process images in batches to prevent memory crash
const processImagesInBatches = async (photos, batchSize = 5) => {
  const results = [];
  for (let i = 0; i < photos.length; i += batchSize) {
    const batch = photos.slice(i, i + batchSize);
    const batchResults = await Promise.all(
      batch.map(async (photo) => {
        const orientation = await getImageOrientation(photo);
        return { photo, ...orientation };
      })
    );
    results.push(...batchResults);
    // Small delay between batches to allow garbage collection
    if (i + batchSize < photos.length) {
      await new Promise(r => setTimeout(r, 50));
    }
  }
  return results;
};

// Layout options for pages
export const LAYOUTS = [
  { 
    id: 'single', 
    name: 'Një foto (Full Page)', 
    slots: [{ x: 0, y: 0, width: 100, height: 100 }],
    icon: '▢'
  },
  { 
    id: 'two-horizontal', 
    name: 'Dy foto horizontale', 
    slots: [
      { x: 5, y: 5, width: 90, height: 43 },
      { x: 5, y: 52, width: 90, height: 43 }
    ],
    icon: '▭▭'
  },
  {
    id: 'three-top',
    name: 'Tre foto (1 lart, 2 poshtë)', 
    slots: [
      { x: 5, y: 5, width: 90, height: 55 },
      { x: 5, y: 63, width: 43, height: 32 },
      { x: 52, y: 63, width: 43, height: 32 }
    ],
    icon: '▭\n▯▯'
  },
  { 
    id: 'four-grid', 
    name: 'Katër foto (grid)', 
    slots: [
      { x: 5, y: 5, width: 43, height: 43 },
      { x: 52, y: 5, width: 43, height: 43 },
      { x: 5, y: 52, width: 43, height: 43 },
      { x: 52, y: 52, width: 43, height: 43 }
    ],
    icon: '▢▢\n▢▢'
  },
  {
    id: 'collage',
    name: 'Kolazh (5 foto)',
    slots: [
      { x: 5, y: 5, width: 60, height: 45 },
      { x: 68, y: 5, width: 27, height: 45 },
      { x: 5, y: 53, width: 27, height: 42 },
      { x: 35, y: 53, width: 30, height: 42 },
      { x: 68, y: 53, width: 27, height: 42 }
    ],
    icon: '⬛⬜\n⬜⬛⬜'
  }
];

// Single page component - wrapped with memo for performance
const PageEditor = memo(({ page, pageIndex, allPages, isSelected, onSelect, onUpdate, pageRef, onAddPhotos, onRemovePhoto, canAddMorePhotos = true, selectedTextId, onTextSelect, onTextUpdate }) => {
  const { t } = useLanguage();
  const [dragOverSlot, setDragOverSlot] = useState(null);
  const [activeSlot, setActiveSlot] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [editingTextId, setEditingTextId] = useState(null);
  const fileInputRef = useRef(null);
  const pageContentRef = useRef(null);
  const dragStateRef = useRef(null);
  const slotRefs = useRef({});
  const layout = LAYOUTS.find(l => l.id === page.layout) || LAYOUTS[0];

  // Keep refs to latest values to avoid stale closures in event listeners
  const onUpdateRef = useRef(onUpdate);
  useEffect(() => { onUpdateRef.current = onUpdate; }, [onUpdate]);
  const pageDataRef = useRef(page);
  useEffect(() => { pageDataRef.current = page; }, [page]);

  // Minimum zoom needed so a cover-fit photo, rotated by any angle (not just
  // 90/180/270 from the old step-rotate buttons - the free-rotate handle
  // below can land anywhere), still fully covers the slot with no
  // whitespace at the corners. Reduces to the old Math.max(w/h, h/w) at
  // exactly 90/270 and to 1 at 0/180.
  const getMinZoom = (slotIndex, rotation) => {
    const slotEl = slotRefs.current[slotIndex];
    if (!slotEl) return 1;
    const { width: w, height: h } = slotEl.getBoundingClientRect();
    const rad = ((rotation || 0) * Math.PI) / 180;
    const c = Math.abs(Math.cos(rad));
    const s = Math.abs(Math.sin(rad));
    return Math.max((w * c + h * s) / w, (w * s + h * c) / h);
  };

  // Zoom change
  const handleZoom = (e, slotIndex, delta) => {
    e.stopPropagation();
    e.preventDefault();
    const img = page.images[slotIndex];
    if (!img) return;
    const minZoom = getMinZoom(slotIndex, img.rotation || 0);
    const newZoom = Math.max(minZoom, Math.min(3.5, (img.zoom || 1) + delta));
    const newImages = {
      ...page.images,
      [slotIndex]: {
        ...img,
        zoom: newZoom,
        panX: newZoom <= minZoom ? 0 : img.panX,
        panY: newZoom <= minZoom ? 0 : img.panY,
      }
    };
    onUpdate(pageIndex, { ...page, images: newImages });
  };

  // Rotate image by 90 degrees (delta: +90 for right, -90 for left)
  const handleRotate = (e, slotIndex, delta = 90) => {
    e.stopPropagation();
    e.preventDefault();
    const img = page.images[slotIndex];
    if (!img) return;
    const newRotation = ((img.rotation || 0) + delta + 360) % 360;
    const minZoom = getMinZoom(slotIndex, newRotation);
    const newZoom = Math.max(minZoom, img.zoom || 1);
    const newImages = {
      ...page.images,
      [slotIndex]: { ...img, rotation: newRotation, zoom: newZoom, panX: 0, panY: 0 }
    };
    onUpdate(pageIndex, { ...page, images: newImages });
  };

  // Reset photo to its best-fit crop (no zoom/pan/rotation)
  const handleFitPhoto = (e, slotIndex) => {
    e.stopPropagation();
    e.preventDefault();
    const img = page.images[slotIndex];
    if (!img) return;
    const newImages = {
      ...page.images,
      [slotIndex]: { ...img, zoom: 1, panX: 0, panY: 0, rotation: 0 }
    };
    onUpdate(pageIndex, { ...page, images: newImages });
  };

  // Pan photo within slot (clamped - no white space)
  const handlePanStart = useCallback((e, slotIndex) => {
    if (!pageDataRef.current.images[slotIndex]) return;
    if (e.type === 'mousedown' && e.button !== 0) return;
    e.preventDefault();
    e.stopPropagation();

    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    const img = pageDataRef.current.images[slotIndex];

    if (e.touches && e.touches.length === 2) {
      // Pinch zoom start
      const dist = Math.hypot(
        e.touches[0].clientX - e.touches[1].clientX,
        e.touches[0].clientY - e.touches[1].clientY
      );
      dragStateRef.current = {
        type: 'pinch',
        slotIndex,
        startDist: dist,
        startZoom: img?.zoom || 1,
      };
    } else {
      // Pan photo within slot
      const slotEl = slotRefs.current[slotIndex];
      const slotRect = slotEl ? slotEl.getBoundingClientRect() : { width: 200, height: 200 };
      const rotation = img.rotation || 0;
      let minZoom = 1;
      if (rotation === 90 || rotation === 270) {
        minZoom = Math.max(slotRect.width / slotRect.height, slotRect.height / slotRect.width);
      }
      // No auto-zoom here — a sudden zoom jump right as the customer starts
      // dragging felt like the app was fighting them. Photos still start
      // at their natural best-fit (matching the thumbnail preview); if
      // there's nothing to pan yet (zoom at minZoom), the customer zooms
      // in first with the +/- controls, same as changing zoom any other time.
      const effectiveZoom = img.zoom || 1;

      dragStateRef.current = {
        type: 'pan',
        slotIndex,
        startX: clientX, startY: clientY,
        startPanX: img.panX || 0,
        startPanY: img.panY || 0,
        slotW: slotRect.width,
        slotH: slotRect.height,
        zoom: effectiveZoom,
        panSlotW: slotRect.width,
        panSlotH: slotRect.height,
        rotation,
      };
    }
    setSelectedSlot(slotIndex);
  }, [pageIndex]);

  // Free-rotate handle drag start (Canva-style: drag around the slot's
  // center to rotate to any angle, not just 90° steps).
  const handleRotateStart = useCallback((e, slotIndex) => {
    e.preventDefault();
    e.stopPropagation();

    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    const slotEl = slotRefs.current[slotIndex];
    const img = pageDataRef.current.images[slotIndex];
    if (!slotEl || !img) return;
    const rect = slotEl.getBoundingClientRect();
    const cx = rect.left + rect.width / 2;
    const cy = rect.top + rect.height / 2;
    const startAngle = Math.atan2(clientY - cy, clientX - cx) * (180 / Math.PI);

    dragStateRef.current = {
      type: 'rotateDrag',
      slotIndex,
      cx, cy,
      startAngle,
      startRotation: img.rotation || 0,
    };
    setSelectedSlot(slotIndex);
  }, []);

  // Move entire slot on page
  const handleMoveStart = useCallback((e, slotIndex) => {
    e.preventDefault();
    e.stopPropagation();

    const pageContentEl = pageContentRef.current;
    if (!pageContentEl) return;
    const rect = pageContentEl.getBoundingClientRect();

    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    const currentPage = pageDataRef.current;
    const currentLayout = LAYOUTS.find(l => l.id === currentPage.layout) || LAYOUTS[0];
    const slot = currentPage.slotSizes?.[slotIndex] || currentLayout.slots[slotIndex];

    dragStateRef.current = {
      type: 'move',
      slotIndex,
      startX: clientX, startY: clientY,
      startSlotX: slot.x,
      startSlotY: slot.y,
      startSlotW: slot.width,
      startSlotH: slot.height,
      pageW: rect.width,
      pageH: rect.height,
    };
  }, []);

  // Text element drag/move
  const handleTextDragStart = useCallback((e, textId) => {
    if (e.type === 'mousedown' && e.button !== 0) return;
    e.preventDefault();
    e.stopPropagation();

    const pageContentEl = pageContentRef.current;
    if (!pageContentEl) return;
    const rect = pageContentEl.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    const text = (pageDataRef.current.texts || []).find(t => t.id === textId);
    if (!text) return;

    dragStateRef.current = {
      type: 'text-move',
      textId,
      startX: clientX, startY: clientY,
      startTextX: text.x,
      startTextY: text.y,
      pageW: rect.width,
      pageH: rect.height,
    };
  }, []);

  // Resize handle start - drags the slot frame itself bigger/smaller
  // (the photo, via object-fit:cover, just fills whatever size it becomes).
  const handleResizeStart = useCallback((e, slotIndex, dir) => {
    e.preventDefault();
    e.stopPropagation();

    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    const pageContentEl = pageContentRef.current;
    if (!pageContentEl) return;
    const rect = pageContentEl.getBoundingClientRect();

    const currentPage = pageDataRef.current;
    const currentLayout = LAYOUTS.find(l => l.id === currentPage.layout) || LAYOUTS[0];
    const slot = currentPage.slotSizes?.[slotIndex] || currentLayout.slots[slotIndex];

    dragStateRef.current = {
      type: 'resize',
      slotIndex,
      dir,
      startX: clientX, startY: clientY,
      startSlot: { ...slot },
      pageW: rect.width,
      pageH: rect.height,
    };
  }, []);

  // Global mousemove/touchmove handler
  useEffect(() => {
    const handleMove = (e) => {
      const state = dragStateRef.current;
      if (!state) return;

      // Prevent page scroll on mobile while dragging
      if (e.cancelable) e.preventDefault();

      if (state.type === 'pinch' && e.touches && e.touches.length === 2) {
        const dist = Math.hypot(
          e.touches[0].clientX - e.touches[1].clientX,
          e.touches[0].clientY - e.touches[1].clientY
        );
        const scale = dist / state.startDist;
        const newZoom = Math.max(0.5, Math.min(3.5, state.startZoom * scale));
        const currentPage = pageDataRef.current;
        const img = currentPage.images[state.slotIndex];
        if (!img) return;
        const newImages = { ...currentPage.images, [state.slotIndex]: { ...img, zoom: newZoom } };
        onUpdateRef.current(pageIndex, { ...currentPage, images: newImages });
        return;
      }

      const clientX = e.touches ? e.touches[0].clientX : e.clientX;
      const clientY = e.touches ? e.touches[0].clientY : e.clientY;
      const dx = clientX - state.startX;
      const dy = clientY - state.startY;

      if (state.type === 'rotateDrag') {
        const currentPage = pageDataRef.current;
        const img = currentPage.images[state.slotIndex];
        if (!img) return;
        const currentAngle = Math.atan2(clientY - state.cy, clientX - state.cx) * (180 / Math.PI);
        let newRotation = (state.startRotation + (currentAngle - state.startAngle)) % 360;
        if (newRotation < 0) newRotation += 360;
        const minZoom = getMinZoom(state.slotIndex, newRotation);
        const newZoom = Math.max(minZoom, img.zoom || 1);
        const newImages = {
          ...currentPage.images,
          [state.slotIndex]: { ...img, rotation: newRotation, zoom: newZoom, panX: 0, panY: 0 }
        };
        onUpdateRef.current(pageIndex, { ...currentPage, images: newImages });
      } else if (state.type === 'pan') {
        const currentPage = pageDataRef.current;
        const img = currentPage.images[state.slotIndex];
        if (!img) return;
        const zoom = state.zoom;
        const rotation = state.rotation || 0;
        let maxPanX, maxPanY;
        if (rotation % 180 === 90) {
          // Swapped axes: image H fills slot W direction and vice versa
          maxPanX = Math.max(0, (state.slotH * zoom - state.slotW) / 2);
          maxPanY = Math.max(0, (state.slotW * zoom - state.slotH) / 2);
        } else if (rotation % 90 === 0) {
          maxPanX = state.slotW * (zoom - 1) / 2;
          maxPanY = state.slotH * (zoom - 1) / 2;
        } else {
          // Free (non-axis-aligned) rotation from the rotate handle - the
          // safe pannable region isn't a simple box at these angles, so
          // keep the photo centered rather than risk uncovering a corner.
          maxPanX = 0;
          maxPanY = 0;
        }
        const newPanX = Math.max(-maxPanX, Math.min(maxPanX, state.startPanX + dx));
        const newPanY = Math.max(-maxPanY, Math.min(maxPanY, state.startPanY + dy));
        const newImages = {
          ...currentPage.images,
          [state.slotIndex]: { ...img, zoom: state.zoom, panX: newPanX, panY: newPanY, panSlotW: state.panSlotW, panSlotH: state.panSlotH }
        };
        onUpdateRef.current(pageIndex, { ...currentPage, images: newImages });
      } else if (state.type === 'move') {
        const currentPage = pageDataRef.current;
        const dxPct = (dx / state.pageW) * 100;
        const dyPct = (dy / state.pageH) * 100;
        const newX = Math.max(0, Math.min(100 - state.startSlotW, state.startSlotX + dxPct));
        const newY = Math.max(0, Math.min(100 - state.startSlotH, state.startSlotY + dyPct));
        const slotSizes = {
          ...(currentPage.slotSizes || {}),
          [state.slotIndex]: { x: newX, y: newY, width: state.startSlotW, height: state.startSlotH }
        };
        onUpdateRef.current(pageIndex, { ...currentPage, slotSizes });
      } else if (state.type === 'resize') {
        const currentPage = pageDataRef.current;
        const { dir, startSlot, pageW, pageH } = state;
        const dxPct = (dx / pageW) * 100;
        const dyPct = (dy / pageH) * 100;

        let { x, y, width, height } = startSlot;

        if (dir.includes('e')) width = Math.max(10, startSlot.width + dxPct);
        if (dir.includes('s')) height = Math.max(10, startSlot.height + dyPct);
        if (dir.includes('w')) {
          const newW = Math.max(10, startSlot.width - dxPct);
          x = startSlot.x + startSlot.width - newW;
          width = newW;
        }
        if (dir.includes('n')) {
          const newH = Math.max(10, startSlot.height - dyPct);
          y = startSlot.y + startSlot.height - newH;
          height = newH;
        }

        x = Math.max(0, x);
        y = Math.max(0, y);
        width = Math.min(width, 100 - x);
        height = Math.min(height, 100 - y);

        const slotSizes = { ...(currentPage.slotSizes || {}), [state.slotIndex]: { x, y, width, height } };
        onUpdateRef.current(pageIndex, { ...currentPage, slotSizes });
      } else if (state.type === 'text-move') {
        const currentPage = pageDataRef.current;
        const dxPct = (dx / state.pageW) * 100;
        const dyPct = (dy / state.pageH) * 100;
        const newX = Math.max(0, Math.min(95, state.startTextX + dxPct));
        const newY = Math.max(0, Math.min(95, state.startTextY + dyPct));
        const texts = (currentPage.texts || []).map(t =>
          t.id === state.textId ? { ...t, x: newX, y: newY } : t
        );
        onUpdateRef.current(pageIndex, { ...currentPage, texts });
      }
    };

    const handleEnd = () => {
      dragStateRef.current = null;
    };

    document.addEventListener('mousemove', handleMove);
    document.addEventListener('mouseup', handleEnd);
    document.addEventListener('touchmove', handleMove, { passive: false });
    document.addEventListener('touchend', handleEnd);

    return () => {
      document.removeEventListener('mousemove', handleMove);
      document.removeEventListener('mouseup', handleEnd);
      document.removeEventListener('touchmove', handleMove);
      document.removeEventListener('touchend', handleEnd);
    };
  }, [pageIndex]);

  const handleDrop = (e, slotIndex) => {
    e.preventDefault();
    setDragOverSlot(null);
    const imageData = e.dataTransfer.getData('image');
    if (imageData) {
      const image = JSON.parse(imageData);
      const newImages = { ...page.images, [slotIndex]: image };
      onUpdate(pageIndex, { ...page, images: newImages });
    }
  };

  const handleDragOver = (e, slotIndex) => {
    e.preventDefault();
    setDragOverSlot(slotIndex);
  };

  const handleDragLeave = () => setDragOverSlot(null);

  const removeImage = (slotIndex) => {
    const imageToRemove = page.images[slotIndex];
    const newImages = { ...page.images };
    delete newImages[slotIndex];
    // Reset slot size for this slot too
    const slotSizes = { ...(page.slotSizes || {}) };
    delete slotSizes[slotIndex];
    onUpdate(pageIndex, { ...page, images: newImages, slotSizes });

    // The same photo can be placed in multiple slots/pages. Only drop it from
    // the library (and revoke its blob URL) if no other slot still uses it -
    // otherwise those other placements lose their image (blank slot in the PDF).
    const usedElsewhere = imageToRemove && (allPages || []).some((p, pIdx) => {
      const imagesToCheck = pIdx === pageIndex ? newImages : (p.images || {});
      return Object.values(imagesToCheck).some(img => img && img.id === imageToRemove.id);
    });
    if (imageToRemove && onRemovePhoto && !usedElsewhere) onRemovePhoto(imageToRemove.id);
    setSelectedSlot(null);
  };

  const handleSlotClick = (e, slotIndex) => {
    e.stopPropagation();
    if (!page.images[slotIndex]) {
      if (!canAddMorePhotos) {
        alert(t('pages.alert.slotLimit'));
        return;
      }
      setActiveSlot(slotIndex);
      fileInputRef.current?.click();
    }
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file || activeSlot === null) return;
    if (isUnsupportedRawFile(file)) {
      alert(t('product.alert.rawFormat', { name: file.name }));
      e.target.value = '';
      return;
    }
    const converted = await convertHeicFileToJpeg(file);
    const newPhoto = {
      id: Date.now() + Math.random(),
      src: URL.createObjectURL(converted),
      file: converted,
      name: converted.name
    };
    const newImages = { ...page.images, [activeSlot]: newPhoto };
    onUpdate(pageIndex, { ...page, images: newImages });
    if (onAddPhotos) onAddPhotos([newPhoto]);
    setActiveSlot(null);
    e.target.value = '';
  };

  return (
    <div
      ref={pageRef}
      className={`page-editor ${isSelected ? 'selected' : ''}`}
      onClick={() => { setSelectedSlot(null); onTextSelect(null); setEditingTextId(null); onSelect(pageIndex); }}
    >
      <div className="page-number">{t('pages.pageNumber', { n: pageIndex + 1 })}</div>
      <div
        ref={pageContentRef}
        className="page-content"
        style={{ backgroundColor: page.backgroundColor || '#ffffff' }}
      >
        {layout.slots.map((slot, slotIndex) => {
          const effectiveSlot = page.slotSizes?.[slotIndex] || slot;
          const image = page.images[slotIndex];
          const isSlotSelected = selectedSlot === slotIndex;

          return (
            <div
              key={slotIndex}
              ref={el => { if (el) slotRefs.current[slotIndex] = el; }}
              className={`photo-slot ${dragOverSlot === slotIndex ? 'drag-over' : ''} ${image ? 'has-image' : ''} ${isSlotSelected ? 'slot-selected' : ''}`}
              style={{
                left: `${effectiveSlot.x}%`,
                top: `${effectiveSlot.y}%`,
                width: `${effectiveSlot.width}%`,
                height: `${effectiveSlot.height}%`,
                cursor: image ? (isSlotSelected ? 'grab' : 'pointer') : 'pointer'
              }}
              onClick={(e) => { e.stopPropagation(); if (!image) handleSlotClick(e, slotIndex); else setSelectedSlot(slotIndex); }}
              onMouseDown={(e) => image && isSlotSelected && handlePanStart(e, slotIndex)}
              onTouchStart={(e) => image && isSlotSelected && handlePanStart(e, slotIndex)}
              onDrop={(e) => handleDrop(e, slotIndex)}
              onDragOver={(e) => handleDragOver(e, slotIndex)}
              onDragLeave={handleDragLeave}
            >
              {image ? (
                <>
                  <div className="slot-image-wrapper">
                    <img
                      src={image.src}
                      alt=""
                      draggable={false}
                      className="slot-image-element"
                      style={{
                        objectFit: image.objectFit || 'cover',
                        transform: `translate(${image.panX || 0}px, ${image.panY || 0}px) rotate(${image.rotation || 0}deg) scale(${image.zoom || 1})`
                      }}
                    />
                  </div>
                  {isSlotSelected && (
                    <>
                      {['n','ne','e','se','s','sw','w','nw'].map(dir => (
                        <div
                          key={dir}
                          className={`resize-handle handle-${dir}`}
                          onMouseDown={(e) => handleResizeStart(e, slotIndex, dir)}
                          onTouchStart={(e) => handleResizeStart(e, slotIndex, dir)}
                        />
                      ))}
                      {/* Free-rotate handle - drag around the slot's center
                          to rotate to any angle (Canva-style), separate from
                          the 90°-step buttons in the zoom bar below. */}
                      <div
                        className="rotate-handle"
                        onMouseDown={(e) => handleRotateStart(e, slotIndex)}
                        onTouchStart={(e) => handleRotateStart(e, slotIndex)}
                        title={t('pages.rotateFree')}
                      >⟳</div>
                      {/* Move handle - drag to move entire slot */}
                      <div
                        className="slot-move-handle"
                        onMouseDown={(e) => handleMoveStart(e, slotIndex)}
                        onTouchStart={(e) => handleMoveStart(e, slotIndex)}
                        title={t('pages.moveSlot')}
                      >
                        ✥
                      </div>
                      <div className="slot-zoom-bar">
                        <button
                          onMouseDown={(e) => e.stopPropagation()}
                          onClick={(e) => handleRotate(e, slotIndex, -90)}
                          title={t('pages.rotateLeft90')}
                        >↺</button>
                        <button
                          onMouseDown={(e) => e.stopPropagation()}
                          onClick={(e) => handleRotate(e, slotIndex, 90)}
                          title={t('pages.rotate90')}
                        >↻</button>
                        <button
                          onMouseDown={(e) => e.stopPropagation()}
                          onClick={(e) => handleZoom(e, slotIndex, -0.1)}
                        >−</button>
                        <span>{Math.round((image.zoom || 1) * 100)}%</span>
                        <button
                          onMouseDown={(e) => e.stopPropagation()}
                          onClick={(e) => handleZoom(e, slotIndex, 0.1)}
                        >+</button>
                        <button
                          onMouseDown={(e) => e.stopPropagation()}
                          onClick={(e) => handleFitPhoto(e, slotIndex)}
                          title={t('pages.fitPhoto')}
                        >⤢</button>
                      </div>
                    </>
                  )}
                  <button
                    className="remove-image-btn"
                    onMouseDown={(e) => e.stopPropagation()}
                    onClick={(e) => { e.stopPropagation(); removeImage(slotIndex); }}
                  >×</button>
                </>
              ) : (
                <div className="slot-placeholder">
                  <svg className="slot-icon" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M12 5v14M5 12h14" />
                  </svg>
                  <span className="slot-text">{t('pages.uploadSlot')}</span>
                </div>
              )}
            </div>
          );
        })}
        {/* Text elements on page */}
        {(page.texts || []).map(textEl => {
          const isTextSel = selectedTextId === textEl.id;
          const isEdit = editingTextId === textEl.id;
          return (
            <div
              key={textEl.id}
              className={`text-element${isTextSel ? ' text-element-selected' : ''}`}
              style={{
                left: `${textEl.x}%`,
                top: `${textEl.y}%`,
                width: `${textEl.width || 60}%`,
                zIndex: isTextSel ? 30 : 15,
                cursor: isEdit ? 'text' : 'move',
              }}
              onMouseDown={(e) => { e.stopPropagation(); setSelectedSlot(null); onTextSelect(textEl.id); if (!isEdit) handleTextDragStart(e, textEl.id); }}
              onTouchStart={(e) => { e.stopPropagation(); setSelectedSlot(null); onTextSelect(textEl.id); }}
              onTouchEnd={(e) => {
                e.stopPropagation();
                // Second tap on already-selected text enters edit mode on mobile
                if (isTextSel && !isEdit) {
                  setEditingTextId(textEl.id);
                }
              }}
              onClick={(e) => { e.stopPropagation(); onTextSelect(textEl.id); }}
              onDoubleClick={(e) => { e.stopPropagation(); setEditingTextId(textEl.id); }}
            >
              {isEdit ? (
                <textarea
                  autoFocus
                  value={textEl.text}
                  onChange={(e) => onTextUpdate(textEl.id, { text: e.target.value })}
                  onBlur={() => setEditingTextId(null)}
                  onMouseDown={(e) => e.stopPropagation()}
                  className="text-edit-input"
                  style={{
                    fontFamily: textEl.fontFamily || 'Playfair Display',
                    fontSize: `${textEl.fontSize || 18}px`,
                    color: textEl.color || '#000000',
                    fontWeight: textEl.bold ? 'bold' : 'normal',
                    fontStyle: textEl.italic ? 'italic' : 'normal',
                    textDecoration: textEl.underline ? 'underline' : 'none',
                    textAlign: textEl.align || 'center',
                  }}
                />
              ) : (
                <span style={{
                  display: 'block',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  lineHeight: 1.3,
                  fontFamily: textEl.fontFamily || 'Playfair Display',
                  fontSize: `${textEl.fontSize || 18}px`,
                  color: textEl.color || '#000000',
                  fontWeight: textEl.bold ? 'bold' : 'normal',
                  fontStyle: textEl.italic ? 'italic' : 'normal',
                  textDecoration: textEl.underline ? 'underline' : 'none',
                  textAlign: textEl.align || 'center',
                }}>
                  {textEl.text}
                </span>
              )}
            </div>
          );
        })}
      </div>
      <div className="page-dimensions">A4 (210 × 297 mm)</div>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileSelect}
        style={{ display: 'none' }}
      />
    </div>
  );
});

// "Add photos from phone" pairing modal - shows a QR code + link to a
// standalone mobile page (MobileUploadPage.js) where the customer picks
// photos straight from their phone's camera roll. Those PUT directly to
// Supabase Storage (see src/lib/mobileUploadSession.js); this modal polls
// for new ones every few seconds and feeds them into onAddPhotos exactly
// like a local file pick, so the rest of the app never needs to know a
// photo came from a phone instead of the computer.
const MOBILE_PAIRING_POLL_MS = 3000;
const MOBILE_PAIRING_MAX_LIFETIME_MS = 15 * 60 * 1000; // stop polling if left open unattended

const MobilePairingModal = memo(({ onAddPhotos, onClose }) => {
  const { t } = useLanguage();
  const [sessionId] = useState(() => createMobileSessionId());
  const [addedCount, setAddedCount] = useState(0);
  const [copied, setCopied] = useState(false);
  const processedPathsRef = useRef(new Set());

  const url = getMobileUploadUrl(sessionId);

  useEffect(() => {
    let stopped = false;
    let polling = false;

    const poll = async () => {
      if (polling || stopped) return;
      polling = true;
      try {
        const photos = await listMobileSessionPhotos(sessionId);
        const fresh = photos.filter(p => !processedPathsRef.current.has(p.path));
        for (const p of fresh) {
          processedPathsRef.current.add(p.path);
          try {
            const res = await fetch(p.publicUrl);
            const blob = await res.blob();
            const file = new File([blob], p.name, { type: blob.type || 'image/jpeg' });
            if (stopped) break;
            onAddPhotos([{
              id: Date.now() + Math.random(),
              src: URL.createObjectURL(file),
              file,
              name: file.name,
            }]);
            setAddedCount(c => c + 1);
            deleteMobileSessionPhotos(sessionId, [p.path]);
          } catch (err) {
            console.error('Failed to pull mobile-uploaded photo:', err);
          }
        }
      } catch (err) {
        console.error('Mobile session poll failed:', err);
      } finally {
        polling = false;
      }
    };

    poll();
    const intervalId = setInterval(poll, MOBILE_PAIRING_POLL_MS);
    const timeoutId = setTimeout(() => clearInterval(intervalId), MOBILE_PAIRING_MAX_LIFETIME_MS);
    return () => {
      stopped = true;
      clearInterval(intervalId);
      clearTimeout(timeoutId);
    };
  }, [sessionId, onAddPhotos]);

  const copyLink = () => {
    navigator.clipboard?.writeText(url).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    }).catch(() => {});
  };

  return (
    <div className="mobile-pairing-overlay" onClick={onClose}>
      <div className="mobile-pairing-card" onClick={e => e.stopPropagation()}>
        <button type="button" className="mobile-pairing-close" onClick={onClose} aria-label={t('pages.close')}>×</button>
        <h3>{t('pages.pairingTitle')}</h3>
        <p className="mobile-pairing-subtitle">{t('pages.pairingSubtitle')}</p>
        <div className="mobile-pairing-qr">
          <QRCodeSVG value={url} size={180} />
        </div>
        <div className="mobile-pairing-link-row">
          <input type="text" readOnly value={url} onFocus={e => e.target.select()} />
          <button type="button" onClick={copyLink}>{copied ? t('pages.copied') : t('pages.copy')}</button>
        </div>
        <p className="mobile-pairing-status">
          {addedCount > 0
            ? t('pages.pairingAdded', { count: addedCount })
            : t('pages.pairingWaiting')}
        </p>
      </div>
    </div>
  );
});

// Photo library sidebar - wrapped with memo for performance
const PhotoLibrary = memo(({ photos, onAddPhotos, onDeletePhoto, onGenerateAI, isGenerating, maxPhotos = 75, canAddMorePhotos = true }) => {
  const { t } = useLanguage();
  const fileInputRef = useRef(null);
  const [isLoading, setIsLoading] = useState(false);
  const [pairingOpen, setPairingOpen] = useState(false);

  const handleFileSelect = async (e) => {
    let files = filterOutRawFiles(Array.from(e.target.files));
    if (files.length === 0) { e.target.value = ''; return; }

    // Truncate to remaining capacity instead of silently letting the
    // library exceed maxPhotos (or, previously, giving no feedback at all
    // about which specific photos were skipped and why).
    const remaining = maxPhotos - photos.length;
    if (remaining <= 0) {
      alert(t('pages.alert.maxForProduct', { max: maxPhotos }));
      e.target.value = '';
      return;
    }
    if (files.length > remaining) {
      const skipped = files.slice(remaining);
      alert(t('pages.alert.limitSkipped', {
        max: maxPhotos,
        remaining,
        names: skipped.map(f => `• ${f.name}`).join('\n'),
      }));
      files = files.slice(0, remaining);
    }

    setIsLoading(true);
    
    try {
      // Process files in batches of 10 to prevent memory overload
      const batchSize = 10;
      const allPhotos = [];
      
      for (let i = 0; i < files.length; i += batchSize) {
        const batch = files.slice(i, i + batchSize);
        const convertedBatch = await convertHeicFilesToJpeg(batch);
        const batchPhotos = convertedBatch.map(file => ({
          id: Date.now() + Math.random(),
          src: URL.createObjectURL(file),
          file: file,
          name: file.name
        }));
        allPhotos.push(...batchPhotos);
        
        // Small delay between batches to allow browser to breathe
        if (i + batchSize < files.length) {
          await new Promise(r => setTimeout(r, 100));
        }
      }
      
      onAddPhotos(allPhotos);
    } catch (error) {
      console.error('Error loading photos:', error);
      alert(t('pages.alert.loadError'));
    } finally {
      setIsLoading(false);
    }
    
    // Reset file input
    e.target.value = '';
  };

  const handleDragStart = (e, photo) => {
    // Only serialize essential data - not the full image src (performance)
    e.dataTransfer.setData('image', JSON.stringify({
      id: photo.id,
      src: photo.src,
      name: photo.name
      // Don't serialize 'file' object - it can't be serialized anyway
    }));
  };

  return (
    <div className="photo-library">


      {/* butoni per gjenerimin me AI per pages */}

      
       <div className="library-header">
        <h4>{t('pages.myPhotos')} {photos.length > 0 && <span className="photo-count-badge">{photos.length}</span>}</h4>
        <button
          className="add-photos-btn"
          onClick={() => canAddMorePhotos && !isLoading && fileInputRef.current?.click()}
          disabled={!canAddMorePhotos || isLoading}
          style={{ opacity: (canAddMorePhotos && !isLoading) ? 1 : 0.5, cursor: (canAddMorePhotos && !isLoading) ? 'pointer' : 'not-allowed' }}
        >
          {isLoading ? t('pages.loading') : canAddMorePhotos ? t('pages.addPhotos') : t('pages.maxPhotosShort', { max: maxPhotos })}
        </button>
        <button
          type="button"
          className="add-photos-phone-btn"
          onClick={() => canAddMorePhotos && setPairingOpen(true)}
          disabled={!canAddMorePhotos}
          style={{ opacity: canAddMorePhotos ? 1 : 0.5, cursor: canAddMorePhotos ? 'pointer' : 'not-allowed' }}
        >
           {t('pages.addFromPhone')}
        </button>
        <p className="mesazhi-per-foto">{t('pages.aiHintBefore')} <strong>AI</strong> {t('pages.aiHintAfter')}</p>
        {photos.length >= 3 && (
          <button
            className="ai-generate-btn"
            onClick={onGenerateAI}
            disabled={isGenerating || isLoading}
          >
            {t('pages.aiGenerateBtn')}
          </button>
        )}
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept="image/*"
          onChange={handleFileSelect}
          style={{ display: 'none' }}
        />
      </div> 
      
      
      {/* Only show photo library section when there are photos */}
      {photos.length > 0 && (
        <div className="library-photos">
          {photos.map(photo => (
            <div
              key={photo.id}
              className="library-photo"
              draggable
              onDragStart={(e) => handleDragStart(e, photo)}
            >
              <img
                src={photo.src}
                alt={photo.name}
                loading="lazy"
                decoding="async"
              />
              {onDeletePhoto && (
                <button
                  type="button"
                  className="library-photo-delete"
                  title={t('pages.deletePhotoTitle')}
                  aria-label={t('pages.deletePhotoTitle')}
                  draggable={false}
                  onDragStart={(e) => e.preventDefault()}
                  onClick={(e) => {
                    e.stopPropagation();
                    onDeletePhoto(photo.id);
                  }}
                >
                  ×
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {pairingOpen && (
        <MobilePairingModal onAddPhotos={onAddPhotos} onClose={() => setPairingOpen(false)} />
      )}
    </div>
  );
});

// Memoized page thumbnail component to prevent re-renders
const PageThumbnail = memo(({ page, index, isSelected, onClick, layout, onDragStart, onDragOver, onDrop, onDragEnd, isDragging, isDragOver, onTouchStart, onTouchMove, onTouchEnd }) => {
  const { t } = useLanguage();
  return (
  <div
    data-page-index={index}
    className={`page-thumbnail ${isSelected ? 'active' : ''} ${isDragging ? 'dragging' : ''} ${isDragOver ? 'drag-over' : ''}`}
    draggable
    onClick={onClick}
    onDragStart={onDragStart}
    onDragOver={onDragOver}
    onDrop={onDrop}
    onDragEnd={onDragEnd}
    onTouchStart={onTouchStart}
    onTouchMove={onTouchMove}
    onTouchEnd={onTouchEnd}
  >
    <div
      className="thumbnail-preview"
      style={{ backgroundColor: page.backgroundColor }}
    >
      {layout.slots.map((slot, slotIndex) => (
        <div
          key={slotIndex}
          className={`thumbnail-slot ${page.images[slotIndex] ? 'filled' : ''}`}
          style={{
            left: `${slot.x}%`,
            top: `${slot.y}%`,
            width: `${slot.width}%`,
            height: `${slot.height}%`,
            ...(page.images[slotIndex] && {
              backgroundImage: `url(${page.images[slotIndex].src})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center'
            })
          }}
        />
      ))}
    </div>
    <span className="thumbnail-label">{t('pages.pageNumber', { n: index + 1 })}</span>
  </div>
  );
});

// Combined thumbnail for a paired spread (e.g. "Page 2-3") — two mini page
// previews side by side in one card, matching the open-book spread shown
// in the main editor. Dragging/dropping this card moves both pages as a unit.
const SpreadThumbnail = memo(({ pageA, pageB, indexA, indexB, isSelected, onClick, layoutA, layoutB, onDragStart, onDragOver, onDrop, onDragEnd, isDragging, isDragOver, onTouchStart, onTouchMove, onTouchEnd }) => {
  const { t } = useLanguage();
  const renderMini = (page, layout) => (
    <div className="thumbnail-preview" style={{ backgroundColor: page.backgroundColor }}>
      {layout.slots.map((slot, slotIndex) => (
        <div
          key={slotIndex}
          className={`thumbnail-slot ${page.images[slotIndex] ? 'filled' : ''}`}
          style={{
            left: `${slot.x}%`,
            top: `${slot.y}%`,
            width: `${slot.width}%`,
            height: `${slot.height}%`,
            ...(page.images[slotIndex] && {
              backgroundImage: `url(${page.images[slotIndex].src})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center'
            })
          }}
        />
      ))}
    </div>
  );
  return (
    <div
      data-page-index={indexA}
      className={`page-thumbnail spread-thumbnail ${isSelected ? 'active' : ''} ${isDragging ? 'dragging' : ''} ${isDragOver ? 'drag-over' : ''}`}
      draggable
      onClick={onClick}
      onDragStart={onDragStart}
      onDragOver={onDragOver}
      onDrop={onDrop}
      onDragEnd={onDragEnd}
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
    >
      <div className="spread-thumbnail-pair">
        {renderMini(pageA, layoutA)}
        {renderMini(pageB, layoutB)}
      </div>
      <span className="thumbnail-label">{t('pages.pageRangeNumber', { a: indexA + 1, b: indexB + 1 })}</span>
    </div>
  );
});

// Open-book spread pairing: page 1 is alone on the right (facing the cover's
// inside spread), then pages pair up 2-3, 4-5, 6-7... matching real print
// pagination. Returns 0-based indices, either side may be null (page 1's
// missing left partner, or a trailing odd page's missing right partner).
const getSpreadIndices = (pageIndex) => {
  const pageNumber = pageIndex + 1;
  if (pageNumber === 1) return { left: null, right: 0 };
  const leftPageNumber = pageNumber % 2 === 0 ? pageNumber : pageNumber - 1;
  return { left: leftPageNumber - 1, right: leftPageNumber };
};

const PagesManager = ({ pages, onUpdate, uploadedPhotos, onAddPhotos, onRemovePhoto, pageRefs, maxPages = 77, maxPhotos = 75, coverData, onCoverUpdate, onActivePageChange, pagesPortalTarget, onCloseDrawer, onOpenDrawer }) => {
  const { t } = useLanguage();
  // -1 = cover page is active
  const [selectedPage, setSelectedPage] = useState(coverData ? -1 : 0);

  // Notify parent when active section changes
  const handlePageSelect = (index) => {
    setSelectedPage(index);
    if (onActivePageChange) onActivePageChange(index === -1 ? 'cover' : 'page');
  };
  const [showLayoutPicker, setShowLayoutPicker] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [selectedTextId, setSelectedTextId] = useState(null);
  const [draggedPageIndex, setDraggedPageIndex] = useState(null);
  const [dragOverPageIndex, setDragOverPageIndex] = useState(null);

  // Thumbnail grouping to match the open-book spread in the main editor:
  // page 1 alone, then pairs 2-3/4-5/6-7... A group is an array of one or
  // two original `pages` indices; each group renders as one thumbnail card
  // and is dragged/reordered as a single unit (see reorderPages below).
  const spreadGroups = [];
  if (pages.length > 0) {
    spreadGroups.push([0]);
    let i = 1;
    while (i < pages.length) {
      if (i + 1 < pages.length) {
        spreadGroups.push([i, i + 1]);
        i += 2;
      } else {
        spreadGroups.push([i]);
        i += 1;
      }
    }
  }

  // fromIndex/toIndex are each a group's representative (first) index —
  // dragging a spread thumbnail moves both of its pages together, keeping
  // every group's internal page order intact.
  const reorderPages = (fromIndex, toIndex) => {
    if (fromIndex === toIndex) return;
    const fromGroupIdx = spreadGroups.findIndex(g => g[0] === fromIndex);
    const toGroupIdx = spreadGroups.findIndex(g => g[0] === toIndex);
    if (fromGroupIdx === -1 || toGroupIdx === -1) return;
    const newGroups = [...spreadGroups];
    const [draggedGroup] = newGroups.splice(fromGroupIdx, 1);
    newGroups.splice(toGroupIdx, 0, draggedGroup);
    const newOrder = newGroups.flat();
    const newPages = newOrder.map(origIndex => pages[origIndex]);
    onUpdate(newPages);
    setSelectedPage(newOrder.indexOf(draggedGroup[0]));
  };

  const handlePageDragStart = (index) => (e) => {
    setDraggedPageIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handlePageDragOver = (index) => (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverPageIndex(index);
  };

  const handlePageDrop = (index) => (e) => {
    e.preventDefault();
    if (draggedPageIndex !== null) {
      reorderPages(draggedPageIndex, index);
    }
    setDraggedPageIndex(null);
    setDragOverPageIndex(null);
  };

  const handlePageDragEnd = () => {
    setDraggedPageIndex(null);
    setDragOverPageIndex(null);
  };

  // Get currently selected text (always fresh from pages data)
  const selectedTextEl = selectedTextId
    ? (pages[selectedPage]?.texts || []).find(t => t.id === selectedTextId) || null
    : null;

  // Update selected text properties
  const updateSelectedText = (props) => {
    if (!selectedTextEl) return;
    const currentPage = pages[selectedPage];
    const texts = (currentPage.texts || []).map(t =>
      t.id === selectedTextId ? { ...t, ...props } : t
    );
    updatePage(selectedPage, { ...currentPage, texts });
  };

  // Delete selected text
  const deleteSelectedText = () => {
    if (!selectedTextEl) return;
    const currentPage = pages[selectedPage];
    const texts = (currentPage.texts || []).filter(t => t.id !== selectedTextId);
    updatePage(selectedPage, { ...currentPage, texts });
    setSelectedTextId(null);
  };

  // Update any text element by id (used by PageEditor for inline editing)
  const handleTextUpdate = (textId, props) => {
    const currentPage = pages[selectedPage];
    const texts = (currentPage.texts || []).map(t => t.id === textId ? { ...t, ...props } : t);
    updatePage(selectedPage, { ...currentPage, texts });
  };

  // Calculate page limit based on number of uploaded photos
  // Use maxPages from props (based on product price)
  const canAddPage = pages.length < maxPages;

  // Check if user can add more photos
  const canAddMorePhotos = uploadedPhotos.length < maxPhotos;
  const remainingPhotos = maxPhotos - uploadedPhotos.length;

  // Wrapper for onAddPhotos to enforce limit
  const handleAddPhotosWithLimit = (newPhotos) => {
    if (!canAddMorePhotos) {
      alert(t('pages.alert.maxPhotosLimit', { max: maxPhotos }));
      return;
    }
    // Limit the number of photos to add
    const allowedPhotos = newPhotos.slice(0, remainingPhotos);
    if (allowedPhotos.length < newPhotos.length) {
      alert(t('pages.alert.remainingOnly', { remaining: remainingPhotos, added: allowedPhotos.length, total: newPhotos.length }));
    }
    onAddPhotos(allowedPhotos);
  };

  // Delete a photo directly from the library (the small "x" on each
  // thumbnail). This clears the photo out of any page slot that uses it
  // too, but never deletes the page itself - the slot is just left empty.
  const handleDeleteLibraryPhoto = (photoId) => {
    const usedInPages = pages.some(p =>
      Object.values(p.images || {}).some(img => img && img.id === photoId)
    );
    if (usedInPages) {
      const updatedPages = pages.map(p => {
        const hasPhoto = Object.values(p.images || {}).some(img => img && img.id === photoId);
        if (!hasPhoto) return p;
        const newImages = { ...p.images };
        const newSlotSizes = { ...(p.slotSizes || {}) };
        Object.keys(newImages).forEach(slotKey => {
          if (newImages[slotKey] && newImages[slotKey].id === photoId) {
            delete newImages[slotKey];
            delete newSlotSizes[slotKey];
          }
        });
        return { ...p, images: newImages, slotSizes: newSlotSizes };
      });
      onUpdate(updatedPages);
    }
    // Photo is no longer referenced by any page at this point, so it's
    // always safe to revoke its blob URL.
    if (onRemovePhoto) onRemovePhoto(photoId);
  };

  // AI Auto-Generate Pages Function
  const generatePagesWithAI = async () => {
    if (uploadedPhotos.length < 3) {
      alert(t('pages.alert.minAI'));
      return;
    }

    setIsGenerating(true);
    const startTime = Date.now();

    try {
      // Analyze photos in batches to prevent memory crash
      const photoAnalysis = await processImagesInBatches(uploadedPhotos, 5);

      // Create pages intelligently
      const newPages = [];
      const allPhotos = [...photoAnalysis]; // Keep original order

      // Walk photos in strict upload order and group only consecutive
      // same-orientation runs into layouts - never pull a same-orientation
      // photo from further down the list to complete a group, so the book
      // always reads in the exact order the customer uploaded. Square
      // photos are treated as portrait: forcing them into the wide
      // two-horizontal slots would crop their top/bottom just as badly as a
      // real portrait photo would.
      const orientedPages = [];
      // For the first FULL_PAGE_PORTRAIT_PAGES pages, portrait/square photos
      // get a full page each instead of being grouped 4-up, so the opening
      // of the book leads with large, uncropped photos.
      const FULL_PAGE_PORTRAIT_PAGES = 10;

      let cursor = 0;
      while (cursor < allPhotos.length) {
        const runIsLandscape = allPhotos[cursor].isLandscape && !allPhotos[cursor].isSquare;
        let end = cursor;
        while (
          end < allPhotos.length &&
          (allPhotos[end].isLandscape && !allPhotos[end].isSquare) === runIsLandscape
        ) {
          end++;
        }
        const run = allPhotos.slice(cursor, end);
        cursor = end;

        if (runIsLandscape) {
          // Landscape photos only ever go into the wide two-horizontal
          // slots (2 per page) - never four-grid/collage/single, which
          // would force a portrait-shaped crop and cut off faces at the
          // sides.
          for (let i = 0; i < run.length; i += 2) {
            const pair = run.slice(i, i + 2);
            if (pair.length === 2) {
              orientedPages.push({ layout: 'two-horizontal', photos: pair });
            } else {
              // Lone leftover landscape photo - no other layout has a
              // landscape-shaped slot, and the full-page slot itself is
              // portrait-shaped (A4), so a plain cover-fit would still crop
              // the sides. forceContain makes that single slot letterbox
              // the photo (show it whole, with margins) instead of cropping.
              orientedPages.push({ layout: 'single', photos: pair, forceContain: true });
            }
          }
        } else {
          // Portrait/square photos only ever go full-page or 4-up in a
          // grid - never into the wide two-horizontal slots.
          let i = 0;
          while (i < run.length) {
            if (orientedPages.length < FULL_PAGE_PORTRAIT_PAGES) {
              orientedPages.push({ layout: 'single', photos: [run[i]] });
              i += 1;
              continue;
            }
            const group = run.slice(i, i + 4);
            if (group.length === 4) {
              orientedPages.push({ layout: 'four-grid', photos: group });
            } else {
              // Fewer than 4 left - one full page per photo instead of a
              // grid with empty slots.
              group.forEach(p => {
                orientedPages.push({ layout: 'single', photos: [p] });
              });
            }
            i += group.length;
          }
        }
      }

      // maxPages is a soft target here: keeping every photo's orientation
      // correct (no cropped faces) and preserving upload order matters more
      // than hitting the exact page count, so we deliberately allow going
      // over it when the split requires more pages, instead of falling
      // back to mismatched layouts or reordering photos.
      orientedPages.forEach((pageData, i) => {
        const images = {};
        pageData.photos.forEach((p, idx) => {
          images[idx] = pageData.forceContain ? { ...p.photo, objectFit: 'contain' } : p.photo;
        });
        newPages.push({
          id: Date.now() + i,
          layout: pageData.layout,
          backgroundColor: '#ffffff',
          images: images
        });
      });

      // Minimum loading time of 8 seconds for better UX
      const elapsedTime = Date.now() - startTime;
      const minLoadingTime = 8000; // 8 seconds
      if (elapsedTime < minLoadingTime) {
        await new Promise(resolve => setTimeout(resolve, minLoadingTime - elapsedTime));
      }

      // Update pages
      onUpdate(newPages);
      setSelectedPage(0);
      // On mobile the customer was looking at the drawer (photo library)
      // while this ran — close it so the newly generated pages are what
      // they see immediately, instead of having to close the drawer
      // themselves and wonder if anything happened.
      if (onCloseDrawer) onCloseDrawer();

    } catch (error) {
      console.error('Error generating pages:', error);
      alert(t('pages.alert.aiError'));
    } finally {
      setIsGenerating(false);
    }
  };

  // Add new page
  const addPage = () => {
    if (!canAddPage) {
      alert(t('pages.alert.maxPages', { max: maxPages }));
      return;
    }
    const newPage = {
      id: Date.now(),
      layout: 'single',
      backgroundColor: '#ffffff',
      images: {}
    };
    onUpdate([...pages, newPage]);
    setSelectedPage(pages.length);
  };

  // Remove page
  const removePage = (index) => {
    if (pages.length <= 1) return;

    const pageToRemove = pages[index];
    const newPages = pages.filter((_, i) => i !== index);

    // Get all photos from the page being removed and remove them from the library,
    // but only if they aren't still placed on one of the remaining pages.
    if (pageToRemove && pageToRemove.images && onRemovePhoto) {
      Object.values(pageToRemove.images).forEach(image => {
        if (image && image.id) {
          const usedElsewhere = newPages.some(p =>
            Object.values(p.images || {}).some(img => img && img.id === image.id)
          );
          if (!usedElsewhere) onRemovePhoto(image.id);
        }
      });
    }

    onUpdate(newPages);
    if (selectedPage >= newPages.length) {
      setSelectedPage(newPages.length - 1);
    }
  };

  // Update single page
  const updatePage = (index, updatedPage) => {
    const newPages = [...pages];
    newPages[index] = updatedPage;
    onUpdate(newPages);
  };

  // Change layout of selected page
  const changeLayout = (layoutId) => {
    if (selectedPage !== null) {
      updatePage(selectedPage, {
        ...pages[selectedPage],
        layout: layoutId
        // Images are preserved when changing layout
      });
    }
    setShowLayoutPicker(false);
  };

  // Change background color of selected page
  const changePageBackground = (color) => {
    if (selectedPage !== null) {
      updatePage(selectedPage, {
        ...pages[selectedPage],
        backgroundColor: color
      });
    }
  };

  // Add text to current page
  const addTextToCurrentPage = () => {
    const currentPage = pages[selectedPage];
    const newText = {
      id: Date.now() + Math.random(),
      text: 'Teksti im',
      x: 20, y: 15,
      fontSize: 18,
      fontFamily: 'Playfair Display',
      color: '#000000',
      bold: false, italic: false, underline: false,
      align: 'center',
      width: 60,
    };
    const texts = [...(currentPage.texts || []), newText];
    updatePage(selectedPage, { ...currentPage, texts });
  };

  // Apply text element to all pages
  const applyTextToAllPages = (textEl) => {
    const newPages = pages.map(p => {
      const existingTexts = (p.texts || []).filter(t => t.id !== textEl.id);
      return { ...p, texts: [...existingTexts, { ...textEl }] };
    });
    onUpdate(newPages);
  };

  return (
    <div className={`pages-manager${selectedPage === -1 && coverData ? ' cover-active' : ''}`}>
      {/* AI Generation Loading Overlay */}
      {isGenerating && (
        <div className="ai-loading-overlay">
          <div className="ai-loading-content">
            <div className="ai-spinner">
              <div className="sparkle"></div>
              <div className="sparkle"></div>
              <div className="sparkle"></div>
              <div className="sparkle"></div>
              <div className="sparkle"></div>
              <div className="sparkle"></div>
              <div className="ai-star">
                <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 2L14.5 9.5L22 12L14.5 14.5L12 22L9.5 14.5L2 12L9.5 9.5L12 2Z" />
                </svg>
              </div>
              <div className="orbit-container">
                <div className="orbit-dot"></div>
                <div className="orbit-dot"></div>
                <div className="orbit-dot"></div>
                <div className="orbit-dot"></div>
              </div>
            </div>
            <p className="ai-loading-text">{t('pages.aiGeneratingTitle')}</p>
            <p className="ai-loading-subtext"> <strong className='textiAI'>{t('pages.aiWarningStrong')}</strong>{t('pages.aiWarningRest')}</p>
          </div>
        </div>
      )}

      {/* Photo library + page info/background. On desktop these render
          right here, in flow. On mobile, PhotoBookDesigner.js passes a
          `pagesPortalTarget` DOM node (inside its full-screen drawer) and
          this whole group portals there instead — same state/handlers,
          different DOM location, so the canvas below stays static until
          the customer opens the drawer. */}
      {(() => {
        const secondaryPanel = (
          <div className="pages-secondary-panel">
            {selectedPage !== -1 && (
              <>
                <div className="page-bg-colors">
                  <span>{t('pages.bgLabel')}</span>
                  {['#ffffff', '#f5f5f5', '#fff8e7', '#e8f4f8', '#f8e8e8', '#e8f8e8'].map(color => (
                    <button
                      key={color}
                      className={`page-color-btn ${pages[selectedPage]?.backgroundColor === color ? 'active' : ''}`}
                      style={{ backgroundColor: color }}
                      onClick={() => changePageBackground(color)}
                    />
                  ))}
                </div>
              </>
            )}
            <PhotoLibrary
              photos={uploadedPhotos}
              onAddPhotos={handleAddPhotosWithLimit}
              onDeletePhoto={handleDeleteLibraryPhoto}
              onGenerateAI={generatePagesWithAI}
              isGenerating={isGenerating}
              maxPhotos={maxPhotos}
              canAddMorePhotos={canAddMorePhotos}
            />
          </div>
        );
        return pagesPortalTarget ? createPortal(secondaryPanel, pagesPortalTarget) : secondaryPanel;
      })()}

      {/* Main pages area */}
      <div className="pages-main">

        {/* ── Cover preview (shown when selectedPage === -1) ── */}
        {selectedPage === -1 && coverData && onCoverUpdate && (
          <div className="cover-in-pages-large">
            <CoverPreview coverData={coverData} onUpdate={onCoverUpdate} onOpenDrawer={onOpenDrawer} />
          </div>
        )}

        {/* Current page editor */}
        <div className={`current-page-container${selectedPage === -1 ? ' hidden' : ''}`}>
          {/* Vertical text toolbar - shown when a text element is selected */}
          {selectedTextEl && (
            <div className="text-panel-vertical">
              <div className="text-panel-section">
                <label className="text-panel-label">{t('pages.font')}</label>
                <select
                  className="text-panel-select"
                  value={selectedTextEl.fontFamily || 'Playfair Display'}
                  onChange={(e) => updateSelectedText({ fontFamily: e.target.value })}
                >
                  {['Playfair Display','Great Vibes','Dancing Script','Lora','Montserrat','Oswald','Open Sans'].map(f => (
                    <option key={f} value={f}>{f}</option>
                  ))}
                </select>
              </div>
              <div className="text-panel-section">
                <label className="text-panel-label">{t('pages.fontSize')}</label>
                <input
                  className="text-panel-size"
                  type="number"
                  min="8" max="120"
                  value={selectedTextEl.fontSize || 18}
                  onChange={(e) => updateSelectedText({ fontSize: parseInt(e.target.value) || 18 })}
                />
              </div>
              <div className="text-panel-section">
                <label className="text-panel-label">{t('pages.style')}</label>
                <div className="text-panel-row">
                  <button className={`text-panel-btn${selectedTextEl.bold ? ' active' : ''}`} onClick={() => updateSelectedText({ bold: !selectedTextEl.bold })}><b>B</b></button>
                  <button className={`text-panel-btn${selectedTextEl.italic ? ' active' : ''}`} onClick={() => updateSelectedText({ italic: !selectedTextEl.italic })}><i>I</i></button>
                  <button className={`text-panel-btn${selectedTextEl.underline ? ' active' : ''}`} onClick={() => updateSelectedText({ underline: !selectedTextEl.underline })}><u>U</u></button>
                </div>
              </div>
              <div className="text-panel-section">
                <label className="text-panel-label">{t('pages.alignment')}</label>
                <div className="text-panel-row">
                  <button className={`text-panel-btn${(selectedTextEl.align||'center')==='left' ? ' active' : ''}`} onClick={() => updateSelectedText({ align: 'left' })}>⬅</button>
                  <button className={`text-panel-btn${(selectedTextEl.align||'center')==='center' ? ' active' : ''}`} onClick={() => updateSelectedText({ align: 'center' })}>↔</button>
                  <button className={`text-panel-btn${(selectedTextEl.align||'center')==='right' ? ' active' : ''}`} onClick={() => updateSelectedText({ align: 'right' })}>➡</button>
                </div>
              </div>
              <div className="text-panel-section">
                <label className="text-panel-label">{t('pages.color')}</label>
                <input
                  className="text-panel-color"
                  type="color"
                  value={selectedTextEl.color || '#000000'}
                  onChange={(e) => updateSelectedText({ color: e.target.value })}
                />
              </div>
              <div className="text-panel-section">
                <button className="text-panel-btn-apply" onClick={() => applyTextToAllPages(selectedTextEl)}>{t('pages.applyAllPages')}</button>
              </div>
              <div className="text-panel-section">
                <button className="text-panel-btn-delete" onClick={deleteSelectedText}>{t('pages.deleteText')}</button>
              </div>
            </div>
          )}
          {pages[selectedPage] && (() => {
            // Open-book spread — page 1 alone on the right, then 2-3/4-5/6-7...
            // (see getSpreadIndices), on every screen size: mobile just gets
            // two smaller pages instead of switching to single-page view. A
            // missing side (page 1's left partner, or a trailing odd page's
            // right partner) renders as blank paper.
            const { left, right } = getSpreadIndices(selectedPage);
            const leftIdx = left != null && left < pages.length ? left : null;
            const rightIdx = right != null && right < pages.length ? right : null;
            const renderSlot = (idx) => (idx != null && pages[idx]) ? (
              <PageEditor
                key={pages[idx].id}
                page={pages[idx]}
                pageIndex={idx}
                allPages={pages}
                isSelected={selectedPage === idx}
                onSelect={() => handlePageSelect(idx)}
                onUpdate={updatePage}
                pageRef={el => { if (pageRefs) pageRefs.current[idx] = el; }}
                onAddPhotos={handleAddPhotosWithLimit}
                onRemovePhoto={onRemovePhoto}
                canAddMorePhotos={canAddMorePhotos}
                onApplyTextToAll={applyTextToAllPages}
                selectedTextId={selectedPage === idx ? selectedTextId : null}
                onTextSelect={setSelectedTextId}
                onTextUpdate={handleTextUpdate}
              />
            ) : <div className="page-spread-blank" />;
            return (
              <div className="page-spread">
                {renderSlot(leftIdx)}
                {renderSlot(rightIdx)}
              </div>
            );
          })()}
        </div>
        {/* Pages toolbar (hidden when on cover) */}
        {selectedPage !== -1 && (
          <div className="pages-toolbar">
            <button
              className={`toolbar-action ${!canAddPage ? 'disabled' : ''}`}
              onClick={addPage}
              disabled={!canAddPage}
              title={canAddPage ? t('pages.addPageTitle') : t('pages.addPageLimitTitle', { max: maxPages })}
            >
              {t('pages.addPageBtn', { count: pages.length, max: maxPages })}
            </button>
            <button className="toolbar-action" onClick={() => setShowLayoutPicker(!showLayoutPicker)}>
              {t('pages.changeLayout')}
            </button>
            <button className="toolbar-action" onClick={addTextToCurrentPage}>
              {t('pages.addText')}
            </button>
            {pages.length > 1 && (
              <button className="toolbar-action danger" onClick={() => removePage(selectedPage)}>
                {t('pages.deletePage')}
              </button>
            )}
          </div>
        )}

        {/* Thumbnails strip — starts with Kopertina */}
        <div className="pages-thumbnails">

          {/* Cover thumbnail */}
          {coverData && (
            <div
              className={`page-thumbnail${selectedPage === -1 ? ' active' : ''}`}
              onClick={() => handlePageSelect(-1)}
              style={{ cursor: 'pointer' }}
            >
              <div
                className="thumbnail-preview cover-thumbnail-preview"
                style={{ backgroundColor: coverData.style?.bg || '#f6f4ee' }}
              >
                <span
                  className="cover-thumb-title"
                  style={{
                    color: coverData.style?.titleColor || '#ef6d49',
                    fontFamily: `'${coverData.style?.fontFamily || 'Londrina Solid'}', sans-serif`,
                  }}
                >
                  {(coverData.meta?.title || 'TITULLI').slice(0, 7)}
                </span>
              </div>
              <span className="thumbnail-label">{t('pages.coverThumb')}</span>
            </div>
          )}

          {/* Page thumbnails — grouped into spreads: page 1 alone, then 2-3/4-5/6-7... */}
          {spreadGroups.map((group) => {
            const repIndex = group[0];
            // Touch drag-reorder intentionally not wired up here — capturing
            // touch gestures for it (via touch-action:none) blocked the
            // strip's native horizontal swipe-to-scroll on mobile, which
            // matters far more day-to-day than touch reordering. Reordering
            // by drag still works with a mouse on desktop.
            const dragProps = {
              onDragStart: handlePageDragStart(repIndex),
              onDragOver: handlePageDragOver(repIndex),
              onDrop: handlePageDrop(repIndex),
              onDragEnd: handlePageDragEnd,
              isDragging: draggedPageIndex === repIndex,
              isDragOver: dragOverPageIndex === repIndex && draggedPageIndex !== repIndex,
            };
            if (group.length === 1) {
              const page = pages[repIndex];
              const layout = LAYOUTS.find(l => l.id === page.layout) || LAYOUTS[0];
              return (
                <PageThumbnail
                  key={page.id}
                  page={page}
                  index={repIndex}
                  isSelected={selectedPage === repIndex}
                  onClick={() => handlePageSelect(repIndex)}
                  layout={layout}
                  {...dragProps}
                />
              );
            }
            const [idxA, idxB] = group;
            const pageA = pages[idxA];
            const pageB = pages[idxB];
            const layoutA = LAYOUTS.find(l => l.id === pageA.layout) || LAYOUTS[0];
            const layoutB = LAYOUTS.find(l => l.id === pageB.layout) || LAYOUTS[0];
            return (
              <SpreadThumbnail
                key={`${pageA.id}-${pageB.id}`}
                pageA={pageA}
                pageB={pageB}
                indexA={idxA}
                indexB={idxB}
                isSelected={selectedPage === idxA || selectedPage === idxB}
                onClick={() => handlePageSelect(idxA)}
                layoutA={layoutA}
                layoutB={layoutB}
                {...dragProps}
              />
            );
          })}
          <button className="add-page-thumbnail" onClick={addPage}>
            <span>+</span>
          </button>
        </div>
      </div>

      {/* Layout picker - positioned at bottom on mobile */}
      {showLayoutPicker && (
        <>
          <div className="layout-picker-overlay" onClick={() => setShowLayoutPicker(false)} />
          <div className="layout-picker">
            <div className="layout-picker-header">
              <h4>{t('pages.chooseLayout')}</h4>
              <button className="layout-picker-close" onClick={() => setShowLayoutPicker(false)}>×</button>
            </div>
            <div className="layouts-grid">
              {LAYOUTS.map(layout => (
                <button
                  key={layout.id}
                  className={`layout-option ${pages[selectedPage]?.layout === layout.id ? 'active' : ''}`}
                  onClick={() => changeLayout(layout.id)}
                >
                  <span className="layout-icon">{layout.icon}</span>
                  <span className="layout-name">{t(`pagesLayout.${layout.id}`)}</span>
                </button>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default PagesManager;
